import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import PartnerSearchSection from '@/components/PartnerSearchSection';
import PlayerNav from '@/components/PlayerNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { getSubcategoriesForSport } from '@/utils/sportRules';
import { useAllSubcategoryCounts } from '@/hooks/useAllSubcategoryCounts';

interface EventData {
  id: string;
  title: string;
  sport: string;
  category: string | null;
  subcategory: string | null;
  entry_fee: number | null;
  match_type: string | null;
  rules: string | null;
}

const PartnerSearchPage = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [activeTab, setActiveTab] = useState('my-events');

  const parsedRules = event?.rules ? (() => { try { return JSON.parse(event.rules); } catch { return null; } })() : null;
  const { counts } = useAllSubcategoryCounts(eventId || '', selectedCategory || event?.category || '', subcategories, parsedRules);

  const isSelectedSubcategoryFull = selectedSubcategory ? counts[selectedSubcategory]?.isFull : false;

  useEffect(() => {
    if (!eventId) return;
    const fetchEvent = async () => {
      const { data, error } = await supabase
        .from('events')
        .select('id, title, sport, category, subcategory, entry_fee, match_type, rules')
        .eq('id', eventId)
        .single();

      if (error || !data) {
        console.error('[PartnerSearchPage] Error fetching event:', error);
        navigate('/');
        return;
      }

      setEvent(data);

      // Get subcategories for this sport
      const sportSubs = getSubcategoriesForSport(data.sport as any, data.category || 'masculina');
      if (sportSubs.length > 0) {
        setSubcategories(sportSubs);
      }

      // If event has a single category/subcategory, auto-select
      if (data.category) {
        setSelectedCategory(data.category);
        setCategories([data.category]);
      }
      if (data.subcategory) {
        setSelectedSubcategory(data.subcategory);
        setShowSearch(true);
      }

      // If no subcategories available, show search directly
      if (sportSubs.length === 0 && !data.subcategory) {
        setShowSearch(true);
      }

      setLoading(false);
    };
    fetchEvent();
  }, [eventId, navigate]);

  // Fetch distinct categories from registrations for this event
  useEffect(() => {
    if (!eventId) return;
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('registrations')
        .select('category, subcategory')
        .eq('event_id', eventId)
        .eq('status', 'confirmed');

      if (data) {
        const uniqueCats = [...new Set(data.map(r => r.category).filter(Boolean))] as string[];
        if (uniqueCats.length > 0 && categories.length === 0) {
          setCategories(uniqueCats);
        }
      }
    };
    fetchCategories();
  }, [eventId]);

  const handleContinue = () => {
    setShowSearch(true);
  };

  const handleTabClick = (tab: string) => {
    switch (tab) {
      case 'dashboard': navigate('/'); break;
      case 'events': navigate('/?tab=events'); break;
      case 'my-events': navigate('/?tab=my-events'); break;
      case 'profile': navigate('/?tab=profile'); break;
      default: setActiveTab(tab);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079]">
      <PlayerNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onTabClick={handleTabClick}
      />

      <main className={`container mx-auto ${isMobile ? 'px-3 pt-3 pb-24' : 'px-4 pt-4 pb-20'} max-w-lg`}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-white">
              {language === 'es' ? 'Busco Pareja' : 'Find a Partner'}
            </h1>
            <p className="text-xs text-white/70">{event.title}</p>
          </div>
        </div>

        {!showSearch ? (
          /* Step 1: Select category/subcategory */
          <Card className="bg-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                {language === 'es' ? 'Selecciona tu categoría' : 'Select your category'}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'es'
                  ? 'Elige la categoría en la que buscas pareja para poder emparejarte con otros jugadores.'
                  : 'Choose the category you want to find a partner in to be matched with other players.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {categories.length > 0 && (
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Categoría' : 'Category'}</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar categoría' : 'Select category'} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {subcategories.length > 0 && (
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Subcategoría' : 'Subcategory'}</Label>
                  <Select 
                    value={selectedSubcategory} 
                    onValueChange={(val) => {
                      if (!counts[val]?.isFull) setSelectedSubcategory(val);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar subcategoría' : 'Select subcategory'} />
                    </SelectTrigger>
                    <SelectContent>
                      {subcategories.map(sub => {
                        const info = counts[sub];
                        const isFull = info?.isFull;
                        const hasMax = info?.max !== null && info?.max !== undefined;
                        return (
                          <SelectItem 
                            key={sub} 
                            value={sub} 
                            disabled={isFull}
                            className={isFull ? 'opacity-50' : ''}
                          >
                            <span className="flex items-center gap-2 w-full">
                              <span>{sub}</span>
                              {hasMax && (
                                isFull ? (
                                  <span className="flex items-center gap-1">
                                    <Lock className="h-3 w-3 text-destructive" />
                                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                                      {language === 'es' ? 'LLENO' : 'FULL'}
                                    </Badge>
                                  </span>
                                ) : (
                                  <Badge className="text-[10px] px-1.5 py-0 bg-primary/15 text-primary border-0">
                                    {info.remaining} {language === 'es' ? 'disponibles' : 'available'}
                                  </Badge>
                                )
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                onClick={handleContinue}
                className="w-full"
                disabled={(subcategories.length > 0 && !selectedSubcategory) || isSelectedSubcategoryFull}
              >
                {language === 'es' ? 'Continuar' : 'Continue'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Step 2: Show partner search waitlist */
          <PartnerSearchSection
            eventId={event.id}
            entryFee={event.entry_fee || 0}
            category={selectedCategory || event.category}
            subcategory={selectedSubcategory || event.subcategory}
            isUserRegistered={false}
          />
        )}
      </main>
    </div>
  );
};

export default PartnerSearchPage;
